import { useRef, useEffect, useCallback, useMemo } from 'react';

const useAudio = (audioConfig = {}) => {
  // Create refs for different sound types
  const correctSoundRef = useRef(null);
  const incorrectSoundRef = useRef(null);
  const perfectSoundRef = useRef(null);
  const streakSoundRef = useRef(null);
  const whooshSoundRef = useRef(null);
  const backgroundMusicRef = useRef(null);
  const starSoundRef = useRef(null);

  // Simple overlap prevention
  const lastPlayTime = useRef({});

  // Memoize config to prevent unnecessary re-renders
  const config = useMemo(() => {
    const defaultConfig = {
      correctSounds: ['/sounds/correct.mp3', '/sounds/correct1.mp3', '/sounds/correct2.mp3'],
      incorrectSounds: ['/sounds/incorrect.mp3', '/sounds/incorrect1.mp3', '/sounds/incorrect2.mp3'],
      perfectSound: '/sounds/perfect.mp3',
      streakSound: '/sounds/streak.mp3',
      whooshSound: '/sounds/whoosh.mp3',
      backgroundMusic: '/sounds/background-music.mp3',
      starSound: '/sounds/star.mp3',
      volumes: {
        effects: 0.6,
        background: 0.3
      }
    };
    
    return { ...defaultConfig, ...audioConfig };
  }, [audioConfig]);

  // Very simple play function
  const playSound = useCallback((audioRef, soundKey) => {
    if (!audioRef.current) {
      console.log(`❌ ${soundKey}: No audio element`);
      return;
    }

    // Prevent rapid-fire sounds (except streaks which are important)
    const now = Date.now();
    if (soundKey !== 'streak' && lastPlayTime.current[soundKey] && 
        (now - lastPlayTime.current[soundKey]) < 300) {
      console.log(`⏭️ ${soundKey}: Too soon, skipping`);
      return;
    }

    const audio = audioRef.current;
    console.log(`🔊 ${soundKey}: Attempting to play`, audio.src);

    try {
      audio.currentTime = 0;
      lastPlayTime.current[soundKey] = now;
      
      audio.play().catch(error => {
        console.log(`🚫 ${soundKey}: Play failed -`, error.message);
      });
    } catch (error) {
      console.log(`💥 ${soundKey}: Exception -`, error.message);
    }
  }, []);

  // Play random sound using Audio constructor (more reliable)
  const playRandomAudio = useCallback((sounds, soundKey) => {
    if (!sounds || sounds.length === 0) return;

    // Prevent rapid-fire
    const now = Date.now();
    if (lastPlayTime.current[soundKey] && (now - lastPlayTime.current[soundKey]) < 300) {
      console.log(`⏭️ ${soundKey}: Too soon, skipping`);
      return;
    }

    try {
      const randomSound = sounds[Math.floor(Math.random() * sounds.length)];
      console.log(`🎲 ${soundKey}: Playing`, randomSound);
      
      const audio = new Audio(randomSound);
      audio.volume = config.volumes.effects;
      lastPlayTime.current[soundKey] = now;
      
      audio.play().catch(error => {
        console.log(`🚫 ${soundKey}: Failed -`, error.message);
      });
    } catch (error) {
      console.log(`💥 ${soundKey}: Exception -`, error.message);
    }
  }, [config.volumes.effects]);

  // Set volumes when component mounts
  useEffect(() => {
    const setupAudio = () => {
      console.log('🔧 Setting up audio elements');
      
      // Set volumes for effect sounds
      [correctSoundRef, incorrectSoundRef, perfectSoundRef, streakSoundRef, whooshSoundRef, starSoundRef].forEach((ref, index) => {
        const names = ['correct', 'incorrect', 'perfect', 'streak', 'whoosh', 'star'];
        if (ref.current) {
          ref.current.volume = config.volumes.effects;
          console.log(`✅ ${names[index]}: Ready, src=${ref.current.src}`);
        } else {
          console.log(`❌ ${names[index]}: Not ready`);
        }
      });

      // Background music
      if (backgroundMusicRef.current) {
        backgroundMusicRef.current.volume = config.volumes.background;
        console.log('✅ Background music: Ready');
      }
    };

    // Only set up once with a single delay
    const timer = setTimeout(setupAudio, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [config.volumes.effects, config.volumes.background]); // Fixed: Added missing dependencies

  // Individual sound functions
  const playCorrectSound = useCallback(() => {
    playRandomAudio(config.correctSounds, 'correct');
  }, [playRandomAudio, config.correctSounds]);

  const playIncorrectSound = useCallback(() => {
    playRandomAudio(config.incorrectSounds, 'incorrect');
  }, [playRandomAudio, config.incorrectSounds]);

  const playPerfectSound = useCallback(() => {
    console.log('🎉 PERFECT SOUND CALLED - Using independent Audio');
    
    // Use independent Audio to avoid DOM issues
    try {
      const audio = new Audio(config.perfectSound);
      audio.volume = config.volumes.effects;
      
      console.log('🎉 Playing perfect sound:', config.perfectSound);
      
      audio.play()
        .then(() => {
          console.log('✅ Perfect sound played successfully!');
        })
        .catch(e => {
          console.log('🚫 Perfect sound failed:', e.message);
        });
        
    } catch (e) {
      console.log('💥 Perfect sound exception:', e.message);
    }
  }, [config.perfectSound, config.volumes.effects]);

  const playStreakSound = useCallback(() => {
    console.log('🎯 STREAK SOUND CALLED - Using independent Audio');
    
    // Always use new Audio() for streak sounds to avoid DOM removal issues
    try {
      const audio = new Audio(config.streakSound);
      audio.volume = config.volumes.effects;
      
      console.log('🎯 Playing streak sound:', config.streakSound);
      
      audio.play()
        .then(() => {
          console.log('✅ Streak sound played successfully!');
        })
        .catch(e => {
          console.log('🚫 Streak sound failed:', e.message);
        });
        
    } catch (e) {
      console.log('💥 Streak sound exception:', e.message);
    }
  }, [config.streakSound, config.volumes.effects]);

  const playWhooshSound = useCallback(() => {
    console.log('💨 WHOOSH SOUND CALLED - Using independent Audio');
    
    // Use independent Audio like streak and star sounds to avoid DOM issues
    try {
      const audio = new Audio(config.whooshSound);
      audio.volume = config.volumes.effects;
      
      console.log('💨 Playing whoosh sound:', config.whooshSound);
      
      audio.play()
        .then(() => {
          console.log('✅ Whoosh sound played successfully!');
        })
        .catch(e => {
          console.log('🚫 Whoosh sound failed:', e.message);
        });
        
    } catch (e) {
      console.log('💥 Whoosh sound exception:', e.message);
    }
  }, [config.whooshSound, config.volumes.effects]);

  const playStarSound = useCallback(() => {
    console.log('⭐ STAR SOUND CALLED - Using independent Audio');
    
    // Use independent Audio like streak sound to avoid DOM issues
    try {
      const audio = new Audio(config.starSound);
      audio.volume = config.volumes.effects;
      
      console.log('⭐ Playing star sound:', config.starSound);
      
      audio.play()
        .then(() => {
          console.log('✅ Star sound played successfully!');
        })
        .catch(e => {
          console.log('🚫 Star sound failed:', e.message);
        });
        
    } catch (e) {
      console.log('💥 Star sound exception:', e.message);
    }
  }, [config.starSound, config.volumes.effects]);

  // Background music
  const startBackgroundMusic = useCallback(() => {
    if (!backgroundMusicRef.current) return;
    
    const audio = backgroundMusicRef.current;
    audio.loop = true;
    audio.currentTime = 0;
    
    audio.play().catch(e => console.log('Background music failed:', e.message));
  }, []);

  const stopBackgroundMusic = useCallback(() => {
    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.pause();
      backgroundMusicRef.current.currentTime = 0;
    }
  }, []);

  // Debug function
  const debugAudio = useCallback(() => {
    console.log('🔍 AUDIO DEBUG:');
    console.log('🔍 Configured correct sounds:', config.correctSounds);
    console.log('🔍 Configured incorrect sounds:', config.incorrectSounds);
    console.log('🔍 Other sounds:', {
      perfect: config.perfectSound,
      streak: config.streakSound,
      whoosh: config.whooshSound,
      star: config.starSound,
      background: config.backgroundMusic
    });
    
    const refs = [
      { name: 'correct', ref: correctSoundRef },
      { name: 'incorrect', ref: incorrectSoundRef },
      { name: 'perfect', ref: perfectSoundRef },
      { name: 'streak', ref: streakSoundRef },
      { name: 'whoosh', ref: whooshSoundRef },
      { name: 'star', ref: starSoundRef },
      { name: 'background', ref: backgroundMusicRef }
    ];
    
    refs.forEach(({ name, ref }) => {
      if (ref.current) {
        console.log(`  ${name}: ✅ src=${ref.current.src}, ready=${ref.current.readyState}`);
      } else {
        console.log(`  ${name}: ❌ No element`);
      }
    });
  }, [config]);

  // Audio elements - using first sound from each array
  const AudioElements = () => (
    <>
      <audio ref={correctSoundRef} src={config.correctSounds[0]} preload="metadata" />
      <audio ref={incorrectSoundRef} src={config.incorrectSounds[0]} preload="metadata" />
      <audio ref={perfectSoundRef} src={config.perfectSound} preload="metadata" />
      <audio ref={streakSoundRef} src={config.streakSound} preload="metadata" />
      <audio ref={whooshSoundRef} src={config.whooshSound} preload="metadata" />
      <audio ref={starSoundRef} src={config.starSound} preload="metadata" />
      <audio ref={backgroundMusicRef} src={config.backgroundMusic} preload="none" />
    </>
  );

  return {
    // Main functions
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    
    // Background music
    startBackgroundMusic,
    stopBackgroundMusic,
    
    // Debug
    debugAudio,
    
    // Components
    AudioElements
  };
};

export default useAudio;